/**
 * useCustomLayerGeoJson
 *
 * Lazy-loads a custom layer's GeoJSON via an injected fetcher, with a shared
 * module-level LRU cache (max 5) and request abortion on unmount / key change.
 * The fetcher is injected so the same loading + caching logic backs both the
 * authenticated renderer (workspace API) and the public renderer (token-gated
 * public API). Callers namespace the cache key to avoid collisions between the
 * two sources for the same layer id.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FeatureCollection } from 'geojson';

const MAX_CACHE_SIZE = 5;

interface CacheEntry {
  data: FeatureCollection;
  accessedAt: number;
}

const geoJsonCache = new Map<string, CacheEntry>();

function evictLRU(): void {
  if (geoJsonCache.size <= MAX_CACHE_SIZE) return;

  let oldestKey: string | null = null;
  let oldestTime = Infinity;
  for (const [key, entry] of geoJsonCache) {
    if (entry.accessedAt < oldestTime) {
      oldestTime = entry.accessedAt;
      oldestKey = key;
    }
  }
  if (oldestKey) {
    geoJsonCache.delete(oldestKey);
  }
}

/** Fetcher resolving a layer's GeoJSON; receives an abort signal to cancel in-flight loads. */
export type CustomLayerGeoJsonFetcher = (
  signal: AbortSignal
) => Promise<FeatureCollection>;

export function useCustomLayerGeoJson(
  cacheKey: string,
  fetcher: CustomLayerGeoJsonFetcher
): FeatureCollection | null {
  const [geoJson, setGeoJson] = useState<FeatureCollection | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Hold the latest fetcher in a ref so `load` depends only on the layer key:
  // callers recreate the fetcher per render, but the layer it resolves is fixed.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = useCallback(async () => {
    const cached = geoJsonCache.get(cacheKey);
    if (cached) {
      cached.accessedAt = Date.now();
      setGeoJson(cached.data);
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const data = await fetcherRef.current(controller.signal);
      geoJsonCache.set(cacheKey, { data, accessedAt: Date.now() });
      evictLRU();
      setGeoJson(data);
    } catch {
      // Silently ignore abort errors and fetch failures for map layers.
    }
  }, [cacheKey]);

  useEffect(() => {
    load();
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [load]);

  return geoJson;
}
